
import userService from '@app/service/userService';
import { create } from 'zustand';
import { subscribeWithSelector, persist } from 'zustand/middleware';


import globalStore from './globalStore';
import deviceStore from './deviceStore';
import hotelService from '@app/service/hotelService';
import authService from '@app/service/authService';
import { clearPendingPush } from '@app/utils/pushHotel';
import {
  applyActiveHotel,
  canSwitchHotel,
  lastCheckNotificationKey,
} from '@app/utils/activeHotel';
import {
  LEGACY_USER_STORAGE_KEYS,
  USER_STORAGE_KEY,
} from './storageKeys';

LEGACY_USER_STORAGE_KEYS.forEach((storageKey) => localStorage.removeItem(storageKey));

const debug = false;

/**
 * CSV-02: 내 정보(/my)와 내 접근 호텔(/my/hotels)을 함께 읽어 작업 호텔을 myInfo에 반영합니다.
 * 호텔 목록을 못 읽으면(이전 서버·일시 오류) 기존처럼 소속 호텔 기준으로 동작합니다.
 */
async function fetchMyInfoWithActiveHotel() {
  const res = await userService.getMyInfo();
  const info = res?.data?.data;
  if (!info?.hotel?.hotelId) return { myInfo: info, myHotels: [] };
  try {
    const hotelsRes = await userService.getMyHotels();
    const myHotels = hotelsRes?.data?.data || [];
    return { myInfo: applyActiveHotel(info, myHotels), myHotels };
  } catch (e) {
    debug && console.log('my hotels fallback : ', e?.response?.status);
    return { myInfo: info, myHotels: [] };
  }
}

const userStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        myInfo: null,
        accessToken: null,
        saveLogin: false,
        hotelInfo: null,
        // CSV-02: 내 접근 호텔 목록(isActive = 작업 호텔). 1개 이하면 전환기를 숨깁니다.
        myHotels: [],
        warningSetting: false, //회사 정보 설정 알람 체크
        setAccessToken: (token) => {
          set({ accessToken: token });
        },
        logout: () => {
          set({ accessToken: null, hotelInfo: null, myHotels: [] });
          // CSV-02: 공용 PC에서 다음 사용자가 이전 사용자의 푸시 알림을 이어서 열지 않도록 비웁니다.
          clearPendingPush();
          globalStore.getState().resetStore();
        },
        setSaveLogin: (save) => {
          set({ saveLogin: save });
        },
        // 내정보 조회
        getMyInfo: () => {
          fetchMyInfoWithActiveHotel()
            .then(({ myInfo, myHotels }) => {
              set({ myInfo, myHotels });
            })
            .catch((e) => {
              get().logout();
            });
        },
        // CSV-02: 접근 호텔 목록만 다시 읽기(기본 호텔 변경 뒤)
        reloadMyHotels: () =>
          userService
            .getMyHotels()
            .then((res) => set({ myHotels: res?.data?.data || [] })),
        /**
         * CSV-02: 작업 호텔 전환. 새 토큰을 받고, 이전 호텔 화면 정보가 남지 않도록 내 정보·호텔
         * 정보·공용 화면 상태를 비운 뒤 홈을 새로 엽니다(열린 채팅·실시간 구독도 함께 끊김).
         * 실패하면 예외를 그대로 던져 호출한 화면이 안내합니다(오류 안내는 공통 axios 처리기).
         */
        switchHotel: async (hotelId) => {
          const deviceToken = await deviceStore.getState().ensureDeviceToken();
          if (!deviceToken) throw new Error('Invalid device token');
          const res = await authService.switchHotelScope({
            'user-token': get().accessToken,
            'device-token': deviceToken,
            'scope-type': 'HOTEL',
            'hotel-id': hotelId,
          });
          set({
            accessToken: res.data.token,
            myInfo: null,
            hotelInfo: null,
            myHotels: [],
          });
          globalStore.getState().resetStore();
          window.location.replace('/home');
        },
        // CSV-02: 알림 마지막 확인 저장 키(여러 호텔을 쓰면 작업 호텔별)
        lastCheckNotificationKey: () => {
          const { myInfo, myHotels } = get();
          return lastCheckNotificationKey(
            myInfo?.userNo,
            myInfo?.hotel?.hotelId,
            canSwitchHotel(myHotels),
          );
        },
        // 내호텔 정보 조회
        getMyHotelInfo: () => {
          const hotelId = get()?.myInfo?.hotel?.hotelId;
          if (!hotelId) return;
          hotelService
            ?.getHotelById(hotelId)
            .then((res) => {
              set({ hotelInfo: res?.data?.data });
            })
            .catch((e) => console.log(e));
        },
      }),
      { name: USER_STORAGE_KEY, version: 0 },
    ),
  ),
);

userStore.subscribe(
  (state) => state.accessToken,
  (accessToken) => {
    debug && console.log('accessToken has been changed: ', accessToken);
    if (!accessToken) {
      userStore.setState({
        myInfo: null,
        accessToken: null,
        saveLogin: false,
      });
      return;
    }

    fetchMyInfoWithActiveHotel()
      .then(({ myInfo, myHotels }) => {
        userStore.setState({ myInfo, myHotels });
      })
      .catch((e) => {
        userStore.getState().logout();
      });
  },
);
export default userStore;
